import { useState, useCallback, useEffect } from 'react';
import { applyProjectTableLayoutSchemaMigration } from '@/hooks/useTableColumns';

const STORAGE_KEY = 'project-table-compact-mode';

export interface TableCompactModeState {
  readers: boolean;
  studio: boolean;
}

const DEFAULT_STATE: TableCompactModeState = {
  readers: false,
  studio: false,
};

function loadFromStorage(): TableCompactModeState {
  applyProjectTableLayoutSchemaMigration();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      readers: typeof parsed.readers === 'boolean' ? parsed.readers : false,
      studio: typeof parsed.studio === 'boolean' ? parsed.studio : false,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function saveToStorage(value: TableCompactModeState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export function useTableCompactMode(): {
  compactReaders: boolean;
  compactStudio: boolean;
  setCompactReaders: (value: boolean) => void;
  setCompactStudio: (value: boolean) => void;
} {
  const [state, setState] = useState<TableCompactModeState>(loadFromStorage);

  useEffect(() => {
    saveToStorage(state);
  }, [state]);

  const setCompactReaders = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, readers: value }));
  }, []);

  const setCompactStudio = useCallback((value: boolean) => {
    setState((prev) => ({ ...prev, studio: value }));
  }, []);

  return {
    compactReaders: state.readers,
    compactStudio: state.studio,
    setCompactReaders,
    setCompactStudio,
  };
}
